/**
 * Orquestra a camada de observabilidade de erro do React.
 *
 * É o ponto único de saída de erro do lado React: as costuras de captura chamam
 * `reportError` e não conhecem o SDK. O estado — contadores e buffer — vive nos módulos,
 * e não em instância de componente, para que remontagem da árvore React não reinicie o
 * que é de sessão.
 */
import { isChunkLoadError } from './chunk-load';
import { registerOccurrence, resetDedupeForTests } from './dedupe';
import { buildFingerprint } from './fingerprint';
import { normalizeError } from './normalize-error';
import {
  emitException,
  isClientReady,
  isObservabilityEnabled,
  startObservabilityClient,
} from './posthog-bridge';
import {
  drainBufferedEvents,
  enqueueBufferedEvent,
  resetPreInitBufferForTests,
} from './pre-init-buffer';
import { COMPONENT_STACK_MAX_LENGTH, MESSAGE_MAX_LENGTH, scrubMessage } from './scrub-message';
import { CaptureContext, ExceptionProperties } from './types';

let initialized = false;

/** Dispara o carregamento do cliente e agenda a drenagem do buffer. Idempotente. */
export function initErrorObservability(): void {
  if (initialized) {
    return;
  }
  initialized = true;

  // Sem await: o carregamento do SDK não pode atrasar o primeiro render. O catch é
  // defensivo — o carregamento trata a própria falha e resolve sempre —, mas um `void
  // promise` sem catch no escopo de módulo é exatamente a rejeição não tratada que esta
  // camada existe para não produzir.
  void startObservabilityClient()
    .then(flushPreInitBuffer)
    .catch(() => undefined);
}

/**
 * Entrada de exceção. Nunca lança.
 *
 * As etapas são encadeadas nesta ordem por dependência: a sanitização precede o
 * fingerprint para que o agrupamento use a mensagem já redigida, e o fingerprint precede
 * o dedupe porque é a chave do contador.
 */
export function reportError(value: unknown, context: CaptureContext): void {
  try {
    // Gate desligado é silêncio total: sem emissão, sem contador e sem buffer. Qualquer
    // efeito antes desta linha deixaria rastro de uma telemetria que está desativada.
    if (!isObservabilityEnabled()) {
      return;
    }

    const normalized = normalizeError(value, context.componentStack);
    const message = scrubMessage(normalized.message, MESSAGE_MAX_LENGTH);
    const componentStack = normalized.componentStack
      ? scrubMessage(normalized.componentStack, COMPONENT_STACK_MAX_LENGTH)
      : undefined;

    const fingerprint = buildFingerprint({
      origin: context.origin,
      scope: context.scope,
      name: normalized.name,
      message,
    });

    const { occurrences, shouldEmit } = registerOccurrence(fingerprint);
    if (!shouldEmit) {
      return;
    }

    // Classificação depois do dedupe: a categoria não entra no fingerprint, então
    // classificar antes produziria o mesmo agrupamento e a informação não serviria a
    // nenhuma decisão até a montagem do payload. Lê a mensagem já sanitizada — nenhuma
    // classe redigida aparece no texto do runtime do webpack, e assim nenhuma etapa
    // depois da sanitização precisa ver texto cru.
    const chunkLoad = isChunkLoadError(normalized.name, message);

    const properties: ExceptionProperties = {
      origem: context.origin,
      // `escopo` participa sempre do fingerprint, mas só vira propriedade quando
      // declarado: emitir a chave vazia criaria uma dimensão sem leitura no painel.
      ...(context.scope ? { escopo: context.scope } : {}),
      // Classificação é transversal: acrescenta dimensão, nunca substitui `origem`.
      ...(chunkLoad ? { categoria: 'chunk_load' as const } : {}),
      fingerprint,
      ocorrencias: occurrences,
      ...(componentStack ? { component_stack: componentStack } : {}),
    };

    // O valor original, e não o normalizado, é o que carrega o stack nativo até o SDK.
    if (isClientReady()) {
      emitException(normalized.original, properties);
      return;
    }

    enqueueBufferedEvent({ error: normalized.original, properties });
  } catch {
    // Uma costura de captura que lança transforma um erro em dois, e o segundo cai num
    // caminho onde não há mais ninguém para capturá-lo. Sem log: o pipeline roda no
    // caminho de erro, e um `console` aqui pode ele mesmo disparar a autocaptura do SDK.
  }
}

/**
 * Drena o buffer e só então consulta a disponibilidade do cliente.
 *
 * A ordem é o que faz um carregamento concluído sem cliente descartar o lote: checar
 * antes deixaria os eventos retidos presos na fila para o resto da sessão.
 */
function flushPreInitBuffer(): void {
  const events = drainBufferedEvents();

  if (!isClientReady()) {
    return;
  }

  for (const event of events) {
    emitException(event.error, event.properties);
  }
}

/** Descarta o estado de módulo da camada entre testes. Não usar em código de produção. */
export function resetErrorObservabilityForTests(): void {
  initialized = false;
  resetDedupeForTests();
  resetPreInitBufferForTests();
}
